import React, { useEffect } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { initDataLayerEvents, cleanupDataLayerEvents } from '../../lib/dataLayerEvents';
import { useSite } from '../../contexts/SiteContext';

/**
 * TrackingPixel Component
 * Loads Google Tag Manager and initializes dataLayer with automatic event tracking.
 * Automatically uses the correct GTM Container ID based on the current site (KSA/UAE).
 * 
 * Sitecore Fields:
 * - gtmContainerId: Optional override for the GTM Container ID
 * - enablePageViews: Enable automatic page view tracking
 * - enableFormTracking: Enable form submission tracking
 * - enableCustomEvents: Enable custom event tracking (clicks, scroll, video, downloads)
 */
const TrackingPixel = ({ fields }) => {
  const site = useSite();
  // Use Sitecore field as override, otherwise use site-level GTM ID
  const gtmId = fields?.gtmContainerId?.value || site.gtmId;
  const enablePageViews = fields?.enablePageViews?.value !== false; // Default true
  const enableFormTracking = fields?.enableFormTracking?.value !== false; // Default true
  const enableCustomEvents = fields?.enableCustomEvents?.value !== false; // Default true

  useEffect(() => {
    if (!gtmId) {
      console.warn('TrackingPixel: GTM Container ID not provided');
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Check if GTM is already loaded
    if (document.getElementById('gtm-script')) {
      return;
    }

    // Load GTM script
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);

    // Add GTM noscript iframe to body
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript';
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);

    // Push site information for segmentation
    window.dataLayer.push({
      site: {
        country: site.country,
        countryCode: site.countryCode,
        currency: site.currency,
        hostname: site.hostname
      }
    });

    // Push initial page view
    if (enablePageViews) {
      window.dataLayer.push({
        event: 'pageview',
        page: {
          path: window.location.pathname,
          title: document.title,
          url: window.location.href,
          country: site.country,
          referrer: document.referrer
        }
      });
    }

    // Initialize automatic event tracking
    if (enableCustomEvents || enableFormTracking) {
      initDataLayerEvents({
        trackClicks: enableCustomEvents,
        trackScrollDepth: enableCustomEvents,
        trackVideoPlays: enableCustomEvents,
        trackFileDownloads: enableCustomEvents,
        trackFormSubmissions: enableFormTracking
      });
    }

    // Cleanup on unmount
    return () => {
      cleanupDataLayerEvents();
    };
  }, [gtmId, site, enablePageViews, enableFormTracking, enableCustomEvents]);

  // This component renders nothing visible
  // The hidden span helps with Sitecore Experience Editor
  return (
    <span style={{ display: 'none' }} data-gtm-container={gtmId}>
      <Text field={fields?.gtmContainerId} />
    </span>
  );
};

export default TrackingPixel;
